import { useState } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { ArrowLeft, CheckCircle, Hammer } from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { api } from "@/lib/api";
import { toast } from "sonner";
import Logo from "@/components/Logo";

export default function RequestAccessPage() {
  const [params] = useSearchParams();
  const [email, setEmail] = useState(params.get("email") || "");
  const [name, setName] = useState("");
  const [reason, setReason] = useState("");
  const [submitted, setSubmitted] = useState(false);
  const [busy, setBusy] = useState(false);
  const navigate = useNavigate();

  const submit = async () => {
    if (!email.includes("@")) {
      toast.error("Enter a valid email");
      return;
    }
    try {
      setBusy(true);
      await api.post("/access/request", {
        email: email.trim().toLowerCase(),
        name: name.trim() || null,
        reason: reason.trim() || null,
      });
      setSubmitted(true);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not submit");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-kraft relative overflow-hidden" data-testid="request-access-page">
      <div
        className="absolute inset-0 opacity-20 bg-cover bg-center"
        style={{
          backgroundImage:
            "url('https://static.prod-images.emergentagent.com/jobs/d521eede-4a6a-4a03-92ae-1c7d49920d93/images/bd7e8813174af2b61844c7a82c5ee0a923880e4bb2e99553afb55190a05c2908.png')",
        }}
        aria-hidden
      />
      <div className="relative z-10 min-h-screen flex flex-col px-6 py-10">
        <div className="flex items-center justify-between">
          <Logo size={24} />
          <Link to="/login" className="text-sm text-timber-700 font-bold flex items-center gap-1" data-testid="back-to-login">
            <ArrowLeft size={16} /> Sign in
          </Link>
        </div>
        <div className="flex-1 flex items-center justify-center">
          <div className="w-full max-w-md tactile-card p-6">
            {submitted ? (
              <div className="text-center" data-testid="waitlist-success">
                <CheckCircle size={56} weight="duotone" className="mx-auto text-ok" />
                <h1 className="font-heading text-2xl font-bold mt-3 text-timber-900">You're on the waitlist.</h1>
                <p className="text-timber-700 mt-2">
                  Thanks. We'll let you know when access becomes available.
                </p>
                <Button
                  data-testid="back-to-login-cta"
                  onClick={() => navigate("/login")}
                  className="mt-6 bg-timber-900 text-masking hover:bg-timber-700 min-h-[52px] px-6 font-bold"
                >
                  Back to sign in
                </Button>
              </div>
            ) : (
              <>
                <div className="flex items-center gap-2">
                  <Hammer size={20} weight="duotone" className="text-masking" />
                  <p className="label-cap">Private beta</p>
                </div>
                <h1 className="font-heading text-3xl font-bold text-timber-900 mt-2 leading-tight">
                  Request access.
                </h1>
                <p className="text-timber-700 mt-2 text-sm">
                  Half Job is invite-only. Tell us a little about you and we'll be in touch.
                </p>

                <div className="mt-5 space-y-3">
                  <div>
                    <Label className="label-cap">Email</Label>
                    <Input
                      data-testid="ra-email-input"
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      placeholder="you@home.com"
                      className="min-h-[56px] mt-1 bg-chalk border-2 border-stone_border rounded-xl text-lg"
                    />
                  </div>
                  <div>
                    <Label className="label-cap">Your name (optional)</Label>
                    <Input
                      data-testid="ra-name-input"
                      value={name}
                      onChange={(e) => setName(e.target.value)}
                      placeholder="Steve"
                      className="min-h-[56px] mt-1 bg-chalk border-2 border-stone_border rounded-xl text-lg"
                    />
                  </div>
                  <div>
                    <Label className="label-cap">Why you want in (optional)</Label>
                    <Textarea
                      data-testid="ra-reason-input"
                      value={reason}
                      onChange={(e) => setReason(e.target.value)}
                      rows={3}
                      placeholder="I've got a half-painted hallway and three other things I've started…"
                      className="mt-1 bg-chalk border-2 border-stone_border rounded-xl text-base"
                    />
                  </div>
                  <Button
                    data-testid="ra-submit-btn"
                    onClick={submit}
                    disabled={busy}
                    className="w-full bg-masking text-timber-900 hover:bg-masking-600 hover:text-white min-h-[56px] text-base font-bold btn-press"
                  >
                    {busy ? "Submitting…" : "Join the waitlist"}
                  </Button>
                  <p className="text-[11px] text-timber-500 text-center mt-2 leading-snug">
                    By joining you agree to our{" "}
                    <a href="/terms" data-testid="ra-terms-link" className="underline hover:text-timber-700">
                      Terms &amp; Acceptable Use
                    </a>.
                  </p>
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
